import * as React from "react";
import {
    Link,
    useNavigate,
    useParams,
    useSearchParams,
} from "react-router-dom";
import { ColumnDef, ExpandedState, Row } from "@tanstack/react-table";
import {
    Badge,
    Button,
    Card,
    SearchInput,
    Table,
} from "@jhool-io/fe-components";
import { useFetchSuperviseesCaseload } from "../../hooks/providers.queries";
import {
    displayNameInRightFormat,
    handleDisplayClientEmailCorrectly,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import { ISuperviseeCaseload } from "../../types/providers.types";
import ListState from "../../../../components/ListState/ListState";
import { NoteStatus } from "../../../notes/types/notes.types";
import MobileListItem from "../../../../shared-ui/MobileListItem/MobileListItem";
import { useAppSelector } from "../../../../hooks/useRedux";
import Loader from "../../../../shared-ui/Loader/Loader";
import ChevronUpIcon from "../../../../icons/ChevronUp";
import ChevronDownIcon from "../../../../icons/ChevronDown";
import { ClientStatus } from "../../../../utils/types/client";

const columns: ColumnDef<ISuperviseeCaseload>[] = [
    {
        accessorKey: "name",
        header: "PROVIDER NAME",
        cell: ({ row }) => (
            <div className="flex gap-[--spacing-items] items-center">
                <Button
                    variant="normal"
                    size="auto"
                    onClick={(e) => {
                        e.stopPropagation();
                        row.toggleExpanded();
                    }}
                    className="size-4 [border:1px_solid_var(--color-text)] rounded-full"
                >
                    {row.getIsExpanded() ? (
                        <ChevronUpIcon />
                    ) : (
                        <ChevronDownIcon />
                    )}
                </Button>
                <span className="font-medium">
                    {displayNameInRightFormat({
                        firstName: row.original.first_name,
                        lastName: row.original.last_name,
                    })}
                </span>
            </div>
        ),
    },
    {
        accessorKey: "credential",
        header: "Credential",
    },
    {
        accessorKey: "email",
        header: "PROVIDER EMAIL",
        cell: ({ row }) =>
            handleDisplayClientEmailCorrectly(row.original.email),
    },
    {
        accessorKey: "delivery_option",
        header: "LOCATIONS",
        cell: ({ row }) =>
            row.original.delivery_option.length > 0
                ? row.original.delivery_option.map((option, i) => {
                      if (i + 1 === row.original.delivery_option.length) {
                          return `${removeEnumUnderscore(
                              makeStringFirstLetterCapital(option)
                          )}`;
                      }
                      return `${removeEnumUnderscore(
                          makeStringFirstLetterCapital(option)
                      )}, `;
                  })
                : "--",
    },
    {
        accessorKey: "contact",
        header: "CONTACT",
    },
    {
        accessorKey: "npi",
        header: "NPI",
        cell: ({ row }) => row.original.npi || "--",
    },
    {
        accessorKey: "active_clients",
        header: "CLIENTS",
    },
    {
        accessorKey: "pending_notes",
        header: "NOTES PENDING APPROVAL",
        cell: ({ row }) => (
            <Link
                to={`/notes?tab=my_supervisees_notes&provider_name=${row.original.first_name} ${row.original.last_name}&status[]=${NoteStatus.PENDING_SUPERVISOR_REVIEW}`}
                className=" text-textLink underline"
                onClick={(e) => e.stopPropagation()}
            >
                {row.original.pending_notes}
            </Link>
        ),
    },
    {
        accessorKey: "appointments",
        header: "APPOINTMENTS",
    },
];

export default function SuperviseeCaseload() {
    const [expandedRows, setExpandedRows] = React.useState<ExpandedState>({});
    const [searchParams, setSearchParams] = useSearchParams();

    const params = useParams();

    const navigate = useNavigate();

    const providerId = params.providerId as string;

    const searchString = searchParams.get("search") || "";

    const { practice } = useAppSelector((state) => state.userPractice);

    const { data, isLoading, error, isSuccess } = useFetchSuperviseesCaseload({
        providerId: providerId || (practice?.provider_id as string),
        search_string: searchString,
    });

    let totalSupervisees = 0;
    let totalClients = 0;
    let totalPendingNotes = 0;

    if (data?.data && data.data.length > 0) {
        totalSupervisees = data.data.length;
        totalClients = data.data.reduce(
            (sum, supervisee) => sum + supervisee.active_clients,
            0
        );
        totalPendingNotes = data.data.reduce(
            (sum, supervisee) => sum + supervisee.pending_notes,
            0
        );
    }

    const getErrorAndLoadingStates = () => {
        if (error) {
            return "0";
        }

        if (isLoading) {
            return <Loader loaderType="inline" />;
        }

        return "";
    };

    const handleRowClick = (row: Row<ISuperviseeCaseload>) => {
        if (practice?.provider_id) return;

        navigate(`/providers/${row.original.provider_id}`);
    };

    const renderRowSubComponent = ({
        row,
    }: {
        row: Row<ISuperviseeCaseload>;
    }) => {
        const clientAppointments = row.original.client_appointments;

        const getClientStatusBadgeType = (clientStatus: string) => {
            switch (clientStatus) {
                case ClientStatus.COMPLETED:
                    return "info";
                case ClientStatus.ACTIVE:
                    return "success";
                case ClientStatus.INACTIVE:
                    return "danger";
                case ClientStatus.PAUSED:
                    return "warning";
                default:
                    return "neutral";
            }
        };

        if (!clientAppointments || clientAppointments.length === 0) {
            return (
                <ListState
                    emptyMessage="No clients for this provider."
                    isError={false}
                    stateHelperText="Clients will appear here when added"
                    emptyBtnProps={{ showButton: false }}
                />
            );
        }

        return (
            <div className="px-[--padding-card-x] py-[--padding-card-y]">
                <div className="grid grid-cols-5 gap-[--spacing-items] text-caption text-textSec mb-[--spacing-items]">
                    <div>CLIENT</div>
                    <div>EMAIL</div>
                    <div>INSURANCE</div>
                    <div>STATUS</div>
                    <div>APPOINTMENTS</div>
                </div>
                {clientAppointments.map(({ client, appointments }) => (
                    <div
                        key={client.client_id}
                        className="grid grid-cols-5 gap-[--spacing-items] text-caption py-[--padding-card-y] text-text font-medium [border-bottom:1px_solid_var(--color-stroke)]"
                    >
                        <div>
                            {displayNameInRightFormat({
                                firstName: client.first_name,
                                lastName: client.last_name,
                            })}
                        </div>
                        <div>{client.email || "--"}</div>
                        <div>{client.insurance_provider_name || "--"}</div>
                        <div>
                            {client.client_status ? (
                                <Badge
                                    content={client.client_status}
                                    type={getClientStatusBadgeType(
                                        client.client_status
                                    )}
                                />
                            ) : (
                                "--"
                            )}
                        </div>
                        <div>{appointments.length}</div>
                    </div>
                ))}
            </div>
        );
    };

    // onChange handler for search input
    const handleSetSearchString = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.value === "") searchParams.delete("search");
        else searchParams.set("search", e.target.value);

        setSearchParams(searchParams);
    };

    return (
        <>
            <Card classNames="mb-[--spacing-items]">
                <SearchInput
                    containerClass="w-[400px]"
                    placeholder="Search"
                    onChange={handleSetSearchString}
                    defaultValue={searchString}
                />
            </Card>
            <Card classNames="grid grid-cols-[repeat(3,minmax(209px,1fr))] overflow-x-auto overflow-y-hidden gap-x-[--spacing-items] mb-[--spacing-header]">
                <Card classNames="bg-[--card-bg-gray] flex flex-col gap-y-2">
                    <div className="font-medium uppercase text-caption">
                        Total Supervisees
                    </div>
                    <div className="font-bold text-title">
                        {getErrorAndLoadingStates() || totalSupervisees}
                    </div>
                </Card>
                <Card classNames="bg-[--card-bg-gray] flex flex-col gap-y-2">
                    <div className="font-medium uppercase text-caption">
                        Total Supervisee Clients
                    </div>
                    <div className="font-bold text-title">
                        {getErrorAndLoadingStates() || totalClients}
                    </div>
                </Card>
                <Card classNames="bg-[--card-bg-gray] flex flex-col gap-y-2">
                    <div className="font-medium uppercase text-caption">
                        Notes Pending Approval
                    </div>
                    <div className="font-bold text-title">
                        {getErrorAndLoadingStates() || totalPendingNotes}
                    </div>
                </Card>
            </Card>
            {isLoading && <Loader />}
            {error && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg={
                        error?.response?.data.message ||
                        `Cannot display supervisees caseload at this time please try again later`
                    }
                />
            )}
            {data && isSuccess && data.data.length === 0 && (
                <ListState
                    listHeader="SUPERVISEES CASELOAD"
                    emptyMessage="No caseload for supervisees yet"
                    isError={false}
                    stateHelperText="Supervisees caseload will appear here when added"
                    emptyBtnProps={{ showButton: false }}
                />
            )}
            {data && isSuccess && data.data.length > 0 && (
                <>
                    <div>
                        <Table
                            columns={columns}
                            data={data.data}
                            handleRowClick={handleRowClick}
                            expandedRows={expandedRows}
                            setExpandedRows={setExpandedRows}
                            renderRowSubComponent={renderRowSubComponent}
                        />
                    </div>
                    <div role="table" className="hidden">
                        <div className="flex flex-col gap-y-12">
                            {data.data.map((supervisee) => (
                                <MobileListItem
                                    key={supervisee.provider_id}
                                    topChildren={
                                        <div className="flex items-center gap-x-8 text-base font-semibold capitalize">
                                            {displayNameInRightFormat({
                                                firstName:
                                                    supervisee.first_name,
                                                lastName: supervisee.last_name,
                                            })}
                                            <span className=" w-1 h-1 rounded-full bg-gray" />
                                            <span className=" text-sm font-medium">
                                                {supervisee.credential}
                                            </span>
                                        </div>
                                    }
                                    bottomChildren={
                                        <Link
                                            className="flex flex-col gap-y-8 w-full items-start"
                                            to={`/providers/${supervisee.provider_id}`}
                                        >
                                            <div className="flex items-center w-full text-left gap-x-4">
                                                <span className="w-1/2 text-gray font-semibold text-xs">
                                                    Email:
                                                </span>
                                                <span className="w-1/2 font-semibold text-xs">
                                                    {supervisee.email}
                                                </span>
                                            </div>
                                            <div className="flex items-center w-full text-left gap-x-4">
                                                <span className="w-1/2 text-gray font-semibold text-xs">
                                                    Locations:
                                                </span>
                                                <span className="w-1/2 font-semibold text-xs capitalize">
                                                    {supervisee.delivery_option.join(
                                                        ","
                                                    )}
                                                </span>
                                            </div>
                                            <div className="flex items-center w-full text-left gap-x-4">
                                                <span className="w-1/2 text-gray font-semibold text-xs">
                                                    Contact:
                                                </span>
                                                <span className="w-1/2 font-semibold text-xs">
                                                    {supervisee.contact}
                                                </span>
                                            </div>
                                            <div className="flex items-center w-full text-left gap-x-4">
                                                <span className="w-1/2 text-gray font-semibold text-xs">
                                                    NPI:
                                                </span>
                                                <span className="w-1/2 font-semibold text-xs">
                                                    {supervisee.npi}
                                                </span>
                                            </div>
                                            <div className="flex items-center w-full text-left gap-x-4">
                                                <span className="w-1/2 text-gray font-semibold text-xs">
                                                    Pending notes:
                                                </span>
                                                <span className="w-1/2 font-semibold text-xs">
                                                    <Link
                                                        onClick={(e) =>
                                                            e.stopPropagation()
                                                        }
                                                        className=" text-primary underline"
                                                        to={`/notes?tab=my_supervisees_notes&provider_name=${supervisee.first_name} ${supervisee.last_name}&status[]=${NoteStatus.PENDING_SUPERVISOR_REVIEW}`}
                                                    >
                                                        {
                                                            supervisee.pending_notes
                                                        }
                                                    </Link>
                                                </span>
                                            </div>
                                        </Link>
                                    }
                                />
                            ))}
                        </div>
                    </div>
                </>
            )}
        </>
    );
}
